import { FetchBookingFragment, PriceCalcSailingsFragment } from "@/gql/graphql";
import {
  MovementType,
  PriceCalculatorFormData,
  SearchEntityType,
} from "../types/types";
import { enterManuallyDefaultOption } from "../components/price-calculator-customer-form/common/PriceCalculatorTimeTableSelect";

export const transformBookingResultToEditableState = ({
  bookingData,
  bookingListData,
  combinedSailings,
  defaultFormState,
  sailings,
  bookingDepartureDate,
  bookingDepartureTime,
}: {
  defaultFormState: PriceCalculatorFormData;
  bookingData: FetchBookingFragment;
  bookingListData: FetchBookingFragment[];
  bookingDepartureDate: string | undefined;
  sailings: PriceCalcSailingsFragment[] | undefined;
  bookingDepartureTime: string | undefined;
  combinedSailings: (PriceCalcSailingsFragment & {
    routeCode: string;
    departureDate: string;
  })[];
}) => {
  return {
    ...defaultFormState,
    selectedCustomerNum: bookingData.customerNumber,
    searchEntityType: SearchEntityType.CUSTOMER,
    movementType:
      bookingData.movementType.code === "S"
        ? MovementType.SECTIONAL
        : MovementType.MULTILEG,
    routeCode:
      bookingData.movementType.code === "S"
        ? bookingData.routeCode?.code
        : bookingData.multiLegRoute?.code,
    multiLegRouteData: bookingData.multiLegRoute ?? undefined,
    routeLegs: bookingListData.map((booking) => {
      const matchingSailing = combinedSailings.find(
        (sailing) =>
          sailing.routeCode === booking.routeCode?.code &&
          sailing.departureDate ===
            booking.sailingBookingConsignment.departureDate &&
          sailing.departureTime ===
            booking.sailingBookingConsignment.departureTime
      );

      return {
        departureDate: booking.sailingBookingConsignment.departureDate ?? "",
        departureTime: booking.sailingBookingConsignment.departureTime ?? "",
        routeCode: booking.sailingBookingConsignment.route.code ?? "",
        sailingTypeCode: booking.sailingBookingConsignment.sailingType ?? "",
        departureTimeManual: !matchingSailing,
      };
    }),
    sailingDate: bookingDepartureDate ?? "",
    departureTime:
      !sailings?.length || sailings === undefined
        ? enterManuallyDefaultOption.departureTime
        : bookingDepartureTime,
    sectionalDepartureTimeManual:
      !sailings?.length || sailings === undefined ? true : false,
    sailingType:
      bookingData.sailingBookingConsignment.sailingType?.charAt(0) ?? undefined,
    vehicleType: bookingData.vehicleType.code,
    vehicleShortDescription: bookingData.vehicleType.shortDescription ?? "",
    vehicleLength: String(bookingData.length),
    vehicleWidth: String(bookingData.width),
    vehicleWeight: bookingData.weight ? String(bookingData.weight) : undefined,
    vehiclePlugins: bookingData.numberOfPlugins ?? undefined,
    vehicleTradeWeight: bookingData.tradeVehicleWeight
      ? String(bookingData.tradeVehicleWeight)
      : "0",
    passengersAdults: bookingData.numberOfAdults ?? undefined,
    passengersChildren: bookingData.numberOfChildren ?? undefined,
    passengersInfants: bookingData.numberOfInfants ?? undefined,
    passengersSleepers: bookingData.numberOfSleepers ?? undefined,
    conditionsGoods: (bookingData.priceCalculatorStatus === "BOOKING"
      ? bookingData.bookingGoods
      : bookingData.goods
    ).map((g) => ({
      goodsCode: g.goodsCode,
      weight: String(g.weight),
    })),
    conditionsAncillary: (bookingData.priceCalculatorStatus === "BOOKING"
      ? bookingData.bookingArticles
      : bookingData.articles
    )
      .filter((ca) => ca.articleType.type === "ANC")
      .map((a) => ({
        ancillaryCode: a.articleCode,
        quantity: Number(a.quantity),
      })),
    moreOptionsPanelCollapsed: false,
    loadingPreference: bookingData.loadingPreferenceCode ?? "",
    cargoHazardousGoods: bookingData?.includeImoClass1Row,
    cargoHazardousGoodsAmountRows: bookingData?.numberOfHazardousGoodsRows,
    cargoHazardousGoodsQualified: bookingData.qualifiedGoods,
    cargoHazardousGoodsTotalWeight: bookingData.hazardousGoodsTotalWeight
      ? String(bookingData.hazardousGoodsTotalWeight)
      : "0",
    livestock: bookingData?.hasLivestock,
    livestockType: bookingData?.livestockType?.code,
    customsClassificationCode: bookingData?.customsClassificationCode ?? "",
    conditionsStandby: bookingData.standby,
    conditionsTransit: bookingData.transit,
    conditionsSenderCountryCode: bookingData.senderCountryCode ?? "",
    conditionsReceiverCountryCode: bookingData.receiverCountryCode ?? "",
  };
};
